/**
 * UserVendorService
 * Service for managing user-vendor associations (composite key)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for user-vendor operations with composite key (userId + vendorId)
 */
export class UserVendorService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? UserVendorService.DEFAULT_SERVICE_PATH,
            basePath: UserVendorService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all user-vendor associations
     * @returns Array of user-vendor associations
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    /**
     * Get all user-vendor associations with full response
     * @returns ResponseData with array of user-vendor associations
     */
    getAllRaw() {
        const url = this.buildUrl();
        return this.http.get(url);
    }
    // ==================== GET BY COMPOSITE ID ====================
    /**
     * Get user-vendor association by composite key
     * @param userId - User ID (string)
     * @param vendorId - Vendor ID
     * @returns The user-vendor association
     */
    async getById(userId, vendorId) {
        const response = await this.getByIdRaw(userId, vendorId);
        if (!response.data) {
            throw new Error(`UserVendors not found: userId=${userId}, vendorId=${vendorId}`);
        }
        return response.data;
    }
    /**
     * Get user-vendor association by composite key with full response
     * @param userId - User ID (string)
     * @param vendorId - Vendor ID
     * @returns ResponseData with user-vendor association
     */
    getByIdRaw(userId, vendorId) {
        const url = this.buildUrl(`${userId}/${vendorId}`);
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new user-vendor association
     * @param data - User-vendor association data
     * @returns The created user-vendor association
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create UserVendors');
        }
        return response.data;
    }
    /**
     * Create a new user-vendor association with full response
     * @param data - User-vendor association data
     * @returns ResponseData with created user-vendor association
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update a user-vendor association
     * @param userId - User ID (string)
     * @param vendorId - Vendor ID
     * @param data - Updated user-vendor association data
     * @returns The updated user-vendor association
     */
    async update(userId, vendorId, data) {
        const response = await this.updateRaw(userId, vendorId, data);
        if (!response.data) {
            throw new Error(`Failed to update UserVendors: userId=${userId}, vendorId=${vendorId}`);
        }
        return response.data;
    }
    /**
     * Update a user-vendor association with full response
     * @param userId - User ID (string)
     * @param vendorId - Vendor ID
     * @param data - Updated user-vendor association data
     * @returns ResponseData with updated user-vendor association
     */
    updateRaw(userId, vendorId, data) {
        const url = this.buildUrl(`${userId}/${vendorId}`);
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Delete a user-vendor association
     * @param userId - User ID (string)
     * @param vendorId - Vendor ID
     * @returns True if deleted successfully
     */
    async delete(userId, vendorId) {
        const response = await this.deleteRaw(userId, vendorId);
        return response.success;
    }
    /**
     * Delete a user-vendor association with full response
     * @param userId - User ID (string)
     * @param vendorId - Vendor ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(userId, vendorId) {
        const url = this.buildUrl(`${userId}/${vendorId}`);
        return this.http.delete(url);
    }
    // ==================== CUSTOM QUERIES ====================
    /**
     * Get all vendors for a specific user
     * @param userId - User ID (string)
     * @returns Array of user-vendor views
     */
    async getByUser(userId) {
        const response = await this.getByUserRaw(userId);
        return response.data ?? [];
    }
    /**
     * Get all vendors for a specific user with full response
     * @param userId - User ID (string)
     * @returns ResponseData with array of user-vendor views
     */
    getByUserRaw(userId) {
        const url = this.buildUrl(`user/${userId}`);
        return this.http.get(url);
    }
    /**
     * Get all users for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of user-vendor views
     */
    async getByVendor(vendorId) {
        const response = await this.getByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all users for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of user-vendor views
     */
    getByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}`);
        return this.http.get(url);
    }
}
UserVendorService.DEFAULT_SERVICE_PATH = 'vendors';
UserVendorService.BASE_PATH = 'api/user-vendors';
//# sourceMappingURL=UserVendorService.js.map